import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { UserOptions as UserOptionsAPI } from "./chat/UserOptions";
import { Link } from "react-router-dom";
import InputText from "./InputText";
import ErrorText from "./ErrorText";
import { PiSignIn } from "react-icons/pi";

interface LoginObj {
  password: string;
  emailId: string;
}

interface FormUpdate {
  updateType: string;
  value: string;
}

function HomePage() {
  const { t } = useTranslation();
  const EARLY_ACCESS: boolean = (process.env.REACT_APP_EARLY_ACCESS || "true").toLowerCase() === "true";
  const INITIAL_LOGIN_OBJ: LoginObj = {
    password: "",
    emailId: "",
  };
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loginObj, setLoginObj] = useState<LoginObj>(INITIAL_LOGIN_OBJ);
  const baseUrl: string = process.env.REACT_APP_BACKEND_URL || "";

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrorMessage("");

    try {
      const email: string = loginObj.emailId.trim();
      const password: string = loginObj.password.trim();
      if (email === "") return setErrorMessage(t('auth.emailPlaceholder'));
      if (password === "") return setErrorMessage(t('auth.password'));
      setLoading(true);
      const response: Response = await fetch(baseUrl + "/users/token/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        setLoading(false);
        return setErrorMessage(t('auth.errorLogin'));
      }

      const data: any = await response.json();

      // Storing tokens in local storage
      localStorage.setItem("accessToken", data.access);
      localStorage.setItem("refreshToken", data.refresh);
      // Fetch and store userOptions after login
      try {
        const userOptions = await UserOptionsAPI();
        localStorage.setItem("userOptions", JSON.stringify(userOptions));
      } catch (err) {
        console.error("Failed to fetch user options after login", err);
      }
      setLoading(false);
      // Redirect to conversations
      window.location.hash = "/conversations";
    } catch (error) {
      setLoading(false);
      return setErrorMessage(t('auth.errorLoggingIn'));
    }
  };

  const updateFormValue = ({ updateType, value }: FormUpdate): void => {
    setErrorMessage("");
    setLoginObj({ ...loginObj, [updateType]: value });
  };

  return (
    <div className="hero h-dvh">
      <div className="hero-content flex-col lg:flex-row-reverse">
        <div className="text-center lg:text-left">
          <h1 className="text-5xl font-bold">{t('app.name')}</h1>
          <h2 className="text-xl font-bold py-6">
            {t('app.tagline')}
          </h2>
        </div>
        <div className="card shrink-0 w-full max-w-sm shadow-2xl bg-base-300 text-base-content">
          <form className="card-body" onSubmit={(e) => handleLogin(e)}>
            <div className="mb-2">
              <InputText
                type="emailId"
                placeholder={t('auth.email')}
                defaultValue={loginObj.emailId}
                updateType="emailId"
                containerStyle="mt-4"
                labelTitle={t('auth.email')}
                updateFormValue={updateFormValue}
              />
              <InputText
                type="Password"
                placeholder={t('auth.password').toLowerCase()}
                defaultValue={loginObj.password}
                updateType="password"
                containerStyle="mt-4"
                labelTitle={t('auth.password')}
                updateFormValue={updateFormValue}
              />
              {/* TODO: FORGOT PASSWORD               <label className="label">
                <a href="#" className="label-text-alt link link-hover">
                  Forgot password?
                </a>
              </label> */}
              <ErrorText styleClass="mt-4">{errorMessage}</ErrorText>
            </div>
            <div className="form-control mt-6">
              <button
                className={"btn btn-primary" + (loading ? " loading" : "")}
              >
                {t('auth.loginButton')}
              </button>
            </div>
            {!EARLY_ACCESS && (
              <div className="text-center mt-4">
                {t('auth.signupPrompt')}{" "}
                <Link to="/signup">
                  <span className="link inline hover:text-primary hover:underline hover:cursor-pointer transition duration-200">
                    {t('auth.signupLink')} <PiSignIn className="inline" />
                  </span>
                </Link>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

export default HomePage;
